import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';

import { api, unwrap } from '@/api/client';
import { TituloBloque } from '@/components/BloquesConsalud';
import { AVATARES } from '@/lib/avatares';
import { fechaLarga, mxn } from '@/lib/formato';
import { animateCSS, drawCheck } from '@/lib/motion';

import type { CheckoutStatus, Ticket } from './queries';

const MAX_INTENTOS = 60; // ~2.5 min

/**
 * `/pagar/resultado` (antes `pago-completado`): NUNCA activa nada. Con Stripe consulta el estado
 * hasta que el webhook registra el pago; con PayPal la captura ya ocurrió y solo lee el ticket.
 */
export function ResultadoPage() {
  const [search] = useSearchParams();
  const sessionId = search.get('session_id');
  const folio = search.get('folio');
  const queryClient = useQueryClient();

  const [inicio] = useState(() => Date.now());
  const { data, isError, dataUpdatedAt } = useQuery({
    queryKey: ['resultado-pago', sessionId, folio],
    queryFn: async (): Promise<CheckoutStatus> => {
      if (folio) {
        const payment = await unwrap<Ticket>(
          api.GET('/api/v1/me/payments/{folio}', { params: { path: { folio } } }),
        );
        return { state: 'completed', payment };
      }
      return unwrap<CheckoutStatus>(
        api.GET('/api/v1/payments/stripe/checkout-sessions/{id}', {
          params: { path: { id: sessionId ?? '' } },
        }),
      );
    },
    enabled: !!(sessionId || folio),
    refetchInterval: (q) =>
      q.state.data?.state === 'completed' ||
      q.state.data?.state === 'expired' ||
      q.state.dataUpdateCount >= MAX_INTENTOS
        ? false
        : 2500,
    retry: false,
  });

  const ticket = data?.state === 'completed' ? data.payment : null;
  useEffect(() => {
    if (ticket) {
      void queryClient.invalidateQueries({ queryKey: ['entitlements'] });
      void queryClient.invalidateQueries({ queryKey: ['mis-licencias'] });
    }
  }, [ticket, queryClient]);

  if (!sessionId && !folio) {
    return (
      <Mensaje
        titulo="No encontramos ese pago"
        texto="Revisa el enlace o consulta tus licencias."
      />
    );
  }
  if (isError) {
    return (
      <Mensaje
        titulo="No encontramos ese pago"
        texto="Si ya pagaste, tu licencia aparecerá en unos minutos en Mis licencias."
      />
    );
  }
  if (data?.state === 'expired') {
    return (
      <Mensaje
        titulo="El pago expiró"
        texto="La sesión de pago venció sin completarse. Puedes intentarlo de nuevo."
      />
    );
  }
  if (ticket) return <TicketPago t={ticket} />;

  // Se deja de consultar tras ~2.5 min (MAX_INTENTOS × 2.5 s).
  const agotado = dataUpdatedAt - inicio >= MAX_INTENTOS * 2500;
  return (
    <section className="panel-vidrio mx-auto max-w-xl text-center" aria-live="polite">
      {!agotado && (
        <span className="mx-auto block h-10 w-10 animate-spin rounded-full border-4 border-cian-500 border-t-transparent" />
      )}
      <h1 className="mt-4 text-xl font-extrabold text-azul-texto">
        {agotado ? 'Tu pago sigue en proceso' : 'Estamos confirmando tu pago'}
      </h1>
      <p className="mt-2 text-texto-2">
        {agotado
          ? 'Te enviaremos un correo en cuanto se confirme. Si pagaste por SPEI puede tardar unos minutos.'
          : 'Esto normalmente toma unos segundos. Esta pantalla se actualiza sola.'}
      </p>
      <Link to="/licencias" className="btn-cs btn-contorno mt-6">
        Ver mis licencias
      </Link>
    </section>
  );
}

function Mensaje({ titulo, texto }: { titulo: string; texto: string }) {
  return (
    <section className="panel-vidrio mx-auto mt-16 max-w-xl text-center">
      <img
        src={AVATARES.doctoraSenala}
        alt=""
        className="pointer-events-none mx-auto -mt-24 mb-2 h-28 w-auto sm:-mt-28 sm:h-32"
      />
      <h1 className="text-xl font-extrabold text-azul-texto">{titulo}</h1>
      <p className="mt-2 text-texto-2">{texto}</p>
      <Link to="/licencias" className="btn-cs btn-primario mt-6">
        Ir a Licencias
      </Link>
    </section>
  );
}

function TicketPago({ t }: { t: Ticket }) {
  const check = useRef<SVGPathElement>(null);
  const tarjeta = useRef<HTMLElement>(null);
  useEffect(() => {
    drawCheck(check.current);
    void animateCSS(tarjeta.current, 'tada');
  }, []);
  const conDescuento = t.finalPrice !== t.regularPrice;
  return (
    <section ref={tarjeta} className="panel-vidrio mx-auto max-w-xl">
      <svg viewBox="0 0 52 52" className="mx-auto h-16 w-16" aria-hidden>
        <circle
          cx="26"
          cy="26"
          r="24"
          fill="none"
          stroke="var(--color-verde-500)"
          strokeWidth="3"
        />
        <path
          ref={check}
          d="M14 27l8 8 16-17"
          fill="none"
          stroke="var(--color-verde-500)"
          strokeWidth="4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <p className="mt-3 text-center text-sm font-extrabold tracking-wide text-verde-texto uppercase">
        Pago completado
      </p>
      <h1 className="mt-1 text-center text-2xl font-extrabold text-azul-texto">
        ¡Tu licencia está activa!
      </h1>
      {/* "Resumen de la compra" del ticket legacy, con la mascota y la línea verde. */}
      <TituloBloque avatar={AVATARES.doctor} className="mt-12">
        Resumen de la compra
      </TituloBloque>
      <dl className="mt-5 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
        <dt className="text-texto-2">Forma de pago</dt>
        <dd className="flex items-center gap-2 font-bold">
          {t.provider === 'stripe' && (
            <img
              src="/img/pagos/stripe.svg"
              alt="Stripe"
              className="h-5 w-auto rounded bg-white px-1"
            />
          )}
          {t.method}
        </dd>
        <dt className="text-texto-2">Folio de compra</dt>
        <dd className="font-mono text-xs break-all">{t.folio}</dd>
        <dt className="text-texto-2">Licencia</dt>
        <dd className="font-bold">{t.license}</dd>
        {conDescuento && (
          <>
            <dt className="text-texto-2">Precio regular</dt>
            <dd className="text-texto-3 line-through">{mxn(t.regularPrice)} MXN</dd>
            <dt className="text-texto-2">Ahorraste</dt>
            <dd className="text-verde-texto">{mxn(t.savings)} MXN</dd>
          </>
        )}
        <dt className="text-texto-2">Total</dt>
        <dd className="text-lg font-extrabold text-cian-texto">{mxn(t.finalPrice)} MXN</dd>
        <dt className="text-texto-2">Vigencia</dt>
        <dd>
          Del {fechaLarga(t.startsOn)} al {fechaLarga(t.endsOn)}
        </dd>
      </dl>
      <p className="mt-4 text-center text-xs text-texto-3">
        Te enviamos el comprobante por correo.
      </p>
      <div className="mt-6 flex flex-wrap justify-center gap-3">
        <Link to="/inicio" className="btn-cs btn-exito">
          Comenzar a estudiar
        </Link>
        <Link to="/licencias" className="btn-cs btn-contorno">
          Mis licencias
        </Link>
      </div>
      <img
        src="/img/consalud-horizontal-color.png"
        alt="Consalud"
        className="solo-claro mx-auto mt-8 h-9 w-auto"
      />
      <img
        src="/img/consalud-horizontal-blanco.png"
        alt="Consalud"
        className="solo-oscuro mx-auto mt-8 h-9 w-auto"
      />
    </section>
  );
}
